import './TrackingSection.css'; // Create this file for custom styling

import React, {
  useEffect,
  useState,
} from 'react';

import {
  get,
  post,
} from 'aws-amplify/api';

// Use individual imports for MUI icons
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PendingIcon from '@mui/icons-material/Pending';
// Use individual imports for MUI components
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.default
}));

const StepIcon = styled('div')(({ theme, status }) => ({
  width: 24,
  height: 24,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: status === 'completed' ? theme.palette.success.main :
         status === 'pending' ? theme.palette.primary.main :
         status === 'error' ? theme.palette.error.main :
         theme.palette.text.disabled
}));

const auditSteps = [
  'Bills Submitted',
  'AI Analysis',
  'Report Generated',
  'Awaiting Approval',
  'Communicating with Utilities',
  'Completed'
];

const TrackingSection = ({ user }) => {
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [approvalRequired, setApprovalRequired] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('info');
  const [processingAction, setProcessingAction] = useState(false);

  useEffect(() => {
    if (user) {
      fetchTrackingData();
      // Polling for updates every 60 seconds
      const interval = setInterval(fetchTrackingData, 60000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchTrackingData = async () => {
    if (!user) {
      console.log('No user available, skipping tracking data fetch');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching tracking data for user:', user.username);
      
      const response = await get({
        apiName: 'UtilityAuditProAPI',
        path: `/tracking`,
        options: {
          queryParams: {
            userId: user.username
          }
        }
      }).response;
      
      if (response.statusCode === 200) {
        const data = await response.body.json();
        console.log('Tracking data:', data);
        
        if (data && data.data) {
          setTrackingData(data.data);
          
          // Set the active step based on the tracking data
          const currentStatus = data.data.status;
          const stepMap = {
            'submitted': 0,
            'analyzing': 1,
            'report_generated': 2,
            'awaiting_approval': 3,
            'communicating': 4,
            'completed': 5
          };
          
          setActiveStep(stepMap[currentStatus] || 0);
          
          // Check if user approval is required
          if (currentStatus === 'awaiting_approval' && data.data.reportData) {
            setReportData(data.data.reportData);
            setApprovalRequired(true);
          } else {
            setApprovalRequired(false);
          }
        }
        
        setError(null);
      } else {
        throw new Error(`API returned status code ${response.statusCode}`);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching tracking data:', err);
      setError('Failed to fetch tracking information. Please try again later.');
      setLoading(false);
    }
  };

  const handleApproveReport = async () => {
    if (!user) {
      setAlertSeverity('error');
      setAlertMessage('User authentication required. Please log in again.');
      setAlertOpen(true);
      return;
    }

    try {
      setProcessingAction(true);
      console.log('Approving report for user:', user.username);
      
      const response = await post({
        apiName: 'UtilityAuditProAPI',
        path: '/tracking/approveReport',
        options: {
          body: {
            userId: user.username,
            reportId: reportData.reportId,
            approved: true
          }
        }
      }).response;
      
      if (response.statusCode === 200) {
        const data = await response.body.json();
        console.log('Approval response:', data);
        
        if (data && data.success) {
          setAlertSeverity('success');
          setAlertMessage('Report approved successfully! Emails are being sent to utility companies.');
          setAlertOpen(true);
          setApprovalRequired(false);
          
          // Refetch data to update the tracking status
          await fetchTrackingData();
        } else {
          throw new Error(data.message || 'Failed to approve report');
        }
      } else {
        throw new Error(`API returned status code ${response.statusCode}`);
      }
    } catch (err) {
      console.error('Error approving report:', err);
      setAlertSeverity('error');
      setAlertMessage(`Failed to approve report: ${err.message}. Please try again later.`);
      setAlertOpen(true);
    } finally {
      setProcessingAction(false);
    }
  };

  const handleRejectReport = async () => {
    if (!user) {
      setAlertSeverity('error');
      setAlertMessage('User authentication required. Please log in again.');
      setAlertOpen(true);
      return;
    }

    try {
      setProcessingAction(true);
      console.log('Rejecting report for user:', user.username);
      
      const response = await post({
        apiName: 'UtilityAuditProAPI',
        path: '/tracking/rejectReport',
        options: {
          body: { 
            userId: user.username,
            reportId: reportData.reportId,
            feedback: 'User requested changes' 
          }
        }
      }).response;
      
      if (response.statusCode === 200) {
        const data = await response.body.json();
        console.log('Rejection response:', data);
        
        if (data && data.success) {
          setAlertSeverity('info');
          setAlertMessage('Report returned for adjustments. Our team will contact you shortly.');
          setAlertOpen(true);
          setApprovalRequired(false);
          
          // Refetch data to update the tracking status
          await fetchTrackingData();
        } else {
          throw new Error(data.message || 'Failed to reject report');
        }
      } else {
        throw new Error(`API returned status code ${response.statusCode}`);
      }
    } catch (err) {
      console.error('Error rejecting report:', err);
      setAlertSeverity('error');
      setAlertMessage(`Failed to process rejection: ${err.message}. Please try again later.`);
      setAlertOpen(true);
    } finally {
      setProcessingAction(false);
    }
  };

  // Determine step status based on current progress
  const getStepStatus = (index) => {
    if (index < activeStep) return 'completed';
    if (index === activeStep) return 'pending';
    return 'waiting';
  };

  // Custom step icon based on status
  const getStepIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleOutlineIcon />;
      case 'pending':
        return <PendingIcon />;
      case 'error':
        return <ErrorOutlineIcon />;
      default:
        return null;
    }
  };

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning">
          User authentication required. Please log in again.
        </Alert>
      </Container>
    );
  }

  if (loading && !trackingData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Loading tracking information...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button 
            variant="contained" 
            onClick={fetchTrackingData}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            disabled={loading}
          >
            Retry
          </Button>
        </Box>
      </Container>
    );
  }

  // If no tracking data is available, show instructions to submit bills first
  if (!trackingData || !trackingData.status) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h5" gutterBottom align="center">
            No Active Audits Found
          </Typography>
          <Typography variant="body1" paragraph align="center">
            Please complete the required forms and submit your utility bills to begin the audit process.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => window.location.hash = 'utility-bills'}
            >
              Go to Utility Bills Section
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Alert for notifications */}
      <Collapse in={alertOpen}>
        <Alert
          severity={alertSeverity}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setAlertOpen(false)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{ mb: 2 }}
        >
          {alertMessage}
        </Alert>
      </Collapse>
      
      {/* Main tracking content */}
      <Paper sx={{ p: 3, borderRadius: 2, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Audit Progress Tracking
        </Typography>
        
        <Stepper 
          activeStep={activeStep} 
          alternativeLabel
          sx={{ mb: 4, mt: 3 }}
        >
          {auditSteps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                StepIconComponent={() => (
                  <StepIcon status={getStepStatus(index)}>
                    {getStepIcon(getStepStatus(index))}
                  </StepIcon>
                )}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
          Current Status: <span style={{ color: '#1976d2', fontWeight: 'bold' }}>{trackingData.statusDescription || trackingData.status}</span>
        </Typography>
        
        {trackingData.estimatedCompletion && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            Estimated completion: {new Date(trackingData.estimatedCompletion).toLocaleDateString()}
          </Typography>
        )}
        
        {trackingData.notes && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {trackingData.notes}
          </Alert>
        )}
      </Paper>
      
      {/* Report approval section */}
      {approvalRequired && reportData && (
        <Paper sx={{ p: 3, borderRadius: 2, mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Report Ready for Your Approval
          </Typography>
          
          <Typography variant="body1" paragraph>
            Our AI has completed the analysis of your utility bills and generated a report. 
            Please review the findings and approve the report to proceed with communication to utility companies.
          </Typography>
          
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <StyledCard>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Analysis Summary
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Typography variant="body2" paragraph>
                    <strong>Issues Found:</strong> {reportData.issuesCount || 0}
                  </Typography>
                  
                  <Typography variant="body2" paragraph>
                    <strong>Potential Savings:</strong> ${reportData.potentialSavings?.toFixed(2) || '0.00'}
                  </Typography>
                  
                  <Typography variant="body2" paragraph>
                    <strong>Analysis Date:</strong> {reportData.analysisDate ? new Date(reportData.analysisDate).toLocaleDateString() : 'N/A'}
                  </Typography>
                </CardContent>
              </StyledCard>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <StyledCard>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Next Steps
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Typography variant="body2" paragraph>
                    Upon your approval, we will send the detailed report to the respective utility companies to request corrections and refunds.
                  </Typography>
                  
                  <Typography variant="body2">
                    You will be kept informed of all communications and progress through the dashboard and email notifications.
                  </Typography>
                </CardContent>
              </StyledCard>
            </Grid>
          </Grid>
          
          {reportData.reportUrl && (
            <Box sx={{ mb: 3 }}>
              <Button 
                variant="outlined" 
                color="primary" 
                href={reportData.reportUrl} 
                target="_blank"
                rel="noopener noreferrer"
                sx={{ mr: 2 }}
              >
                View Full Report
              </Button>
            </Box>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Button 
              variant="outlined" 
              color="secondary" 
              onClick={handleRejectReport}
              disabled={processingAction}
            >
              Request Changes
            </Button>
            
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleApproveReport}
              disabled={processingAction}
              startIcon={processingAction ? <CircularProgress size={20} /> : null}
            >
              Approve & Send to Utilities
            </Button>
          </Box>
        </Paper>
      )}
      
      {/* Historical timeline */}
      {trackingData.timeline && trackingData.timeline.length > 0 && (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            Audit Timeline
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            {trackingData.timeline.map((event, index) => (
              <Box key={index} sx={{ 
                display: 'flex', 
                mb: 2,
                pb: 2,
                borderBottom: index < trackingData.timeline.length - 1 ? '1px dashed #e0e0e0' : 'none'
              }}>
                <Box sx={{ 
                  mr: 2, 
                  minWidth: '80px',
                  textAlign: 'right',
                  color: 'text.secondary',
                  fontSize: '0.875rem'
                }}>
                  {new Date(event.timestamp).toLocaleDateString()}
                </Box>
                
                <Box>
                  <Typography variant="subtitle2">
                    {event.title}
                  </Typography>
                  {event.description && (
                    <Typography variant="body2" color="text.secondary">
                      {event.description}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default TrackingSection;